class VariantPickerCard extends HTMLElement {
  constructor() {
    super();
    this.productCard = null;
    this.variants = [];
    this.selects = [];
    this.mutationObserver = null;
  }

  connectedCallback() {
    this.productCard = this.closest('product-card');
    if (!this.productCard) return;

    // Small delay to ensure DOM is ready
    setTimeout(() => this.init(), 200);

    // Watch for DOM changes (e.g., when section re-renders)
    this.observeDOMChanges();
  }

  disconnectedCallback() {
    // Clean up observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  observeDOMChanges() {
    // Watch for the variant data script being added/removed
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.matches && node.matches('[data-product-card-variants]')) {
            console.log('🔄 Variant data script added, reinitializing...');
            this.init();
          }
        });
      });
    });

    // Observe the product card content for changes
    const contentDiv = this.productCard.querySelector('.product-card__content');
    if (contentDiv) {
      this.mutationObserver.observe(contentDiv, {
        childList: true,
        subtree: true
      });
    }
  }

  init() {
    // Reset state
    this.variants = [];
    this.selects = [];

    // Find the variant data script
    const variantScript = this.findVariantScript();
    
    if (!variantScript) {
      console.log('ℹ️ VariantPickerCard: No variant data found');
      return;
    }

    try {
      this.variants = JSON.parse(variantScript.textContent);
      console.log('✅ VariantPickerCard: Loaded', this.variants.length, 'variants');
      this.style.display = '';
    } catch (e) {
      console.error('❌ VariantPickerCard: Failed to parse:', e);
      return;
    }

    // Setup select listeners safely
    this.setupSelectListeners();

    // Initialize with pre-selected variant
    const initialId = this.dataset.selectedVariantId;
    if (initialId) {
      const initialVariant = this.variants.find(v => v.id.toString() === initialId);
      if (initialVariant) {
        this.selects.forEach(select => {
          const optionIndex = parseInt(select.dataset.optionIndex);
          const optionValue = initialVariant.options[optionIndex];
          const escapedValue = optionValue.replace(/"/g, '&quot;');
          const matchingOption = select.querySelector(`option[value="${escapedValue}"]`);
          if (matchingOption && !matchingOption.disabled) {
            select.value = optionValue;
          }
        });
      }
      this.updateToVariant(initialId, true);
    }

    // Dispatch ready event
    this.dispatchEvent(new CustomEvent('variant-picker:ready', {
      bubbles: true,
      detail: { variantCount: this.variants.length }
    }));
  }

  findVariantScript() {
    return this.productCard.querySelector('[data-product-card-variants]') ||
           this.querySelector('[data-product-card-variants]') ||
           document.querySelector(`[data-product-card-variants]`);
  }

   setupSelectListeners() {
    // Use event delegation for change events
    if (!this.dataset.listenerAttached) {
      this.addEventListener('change', (event) => {
        if (event.target.matches('.variant-picker-card__select')) {
          console.log('🔄 VariantPickerCard: Option changed:', event.target.dataset.optionName, '=', event.target.value);
          this.onVariantChange();
        }
      });
      this.dataset.listenerAttached = 'true';
    }
    
    this.selects = Array.from(this.querySelectorAll('.variant-picker-card__select'));

    // CRITICAL: Prevent the outer slideshow's drag handler from calling
    // preventDefault() on mousedown, which blocks the native <select> from opening.
    this.selects.forEach(select => {
      if (!select.dataset.dragGuard) {
        const stopDrag = (e) => e.stopPropagation();
        
        select.addEventListener('mousedown', stopDrag);
        select.addEventListener('pointerdown', stopDrag);
        // Also prevent clicks on the select from being swallowed by slideshow click prevention
        select.addEventListener('click', stopDrag);
        
        select.dataset.dragGuard = 'true';
      }
    });
    
    console.log('✅ VariantPickerCard: Setup', this.selects.length, 'selects with drag guards');
  }

  onVariantChange() {
    const selected = [];
    this.selects.forEach(s => {
      if (s.value === '') {
        console.log('⚠️ VariantPickerCard: Placeholder option selected, ignoring');
        return;
      }
      selected[parseInt(s.dataset.optionIndex)] = s.value.trim();
    });

    const hasValidSelection = selected.some(val => val !== undefined && val !== '');
    
    if (!hasValidSelection) {
      console.log('⚠️ VariantPickerCard: No valid selection made yet');
      return;
    }

    console.log('🔍 VariantPickerCard: Selected options:', selected);

    const variant = this.variants.find(v => {
      const optionsToCompare = v.options.map((opt, i) => {
        const selectedOption = selected[i];
        if (selectedOption === undefined || selectedOption === '') return true;
        return opt.trim() === selectedOption;
      });
      return optionsToCompare.every(match => match === true);
    });

    if (!variant) {
      console.warn('⚠️ VariantPickerCard: No matching variant found for options:', selected);
      return;
    }

    if (!variant.available) {
      console.warn('⚠️ VariantPickerCard: Variant not available:', variant.title);
      return;
    }

    this.updateToVariant(variant.id.toString());
  }

  updateToVariant(variantId, isInitial = false) {
    const variant = this.variants.find(v => v.id.toString() === variantId.toString());
    if (!variant) {
      console.warn('⚠️ VariantPickerCard: Variant not found for ID:', variantId);
      return;
    }

    console.log('🔄 VariantPickerCard: Switching to:', variant.title, '(ID:', variant.id, ')');

    this.dataset.selectedVariantId = variant.id;
    this.selectedVariant = variant;

    // Update all components safely
    try { this.switchImage(variant, isInitial); } catch (e) { console.error('Error in switchImage:', e); }
    try { this.switchPrice(variant); } catch (e) { console.error('Error in switchPrice:', e); }
    try { this.updateProductForms(variant); } catch (e) { console.error('Error in updateProductForms:', e); }
    try { this.updateProductCardLinks(variant); } catch (e) { console.error('Error in updateProductCardLinks:', e); }

    this.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: { 
        variant: variant,
        productId: this.productCard.dataset.productId
      }
    }));
  }

    switchImage(variant, isInitial = false) {
    const gallery = this.productCard.querySelector('[ref="cardGallery"]');
    if (!gallery) {
      console.warn('⚠️ VariantPickerCard: No card gallery found');
      return;
    }

    // Find the slideshow-slides scroller (where all slides live)
    const scroller = gallery.querySelector('slideshow-slides') ||
                     gallery.querySelector('[ref="scroller"]');
    if (!scroller) {
      console.warn('⚠️ VariantPickerCard: No slideshow scroller found');
      return;
    }

    const allSlides = scroller.querySelectorAll('slideshow-slide');
    if (!allSlides.length) {
      console.warn('⚠️ VariantPickerCard: No slides found');
      return;
    }

    // Find the slide that matches the variant's featured image
    let targetSlide = null;
    if (variant.featured_media_id) {
      targetSlide = scroller.querySelector(
        `slideshow-slide[slide-id="${variant.featured_media_id}"]`
      );
    }

    // Fallback: if variant has no image, or slide not found, use first slide
    if (!targetSlide) {
      targetSlide = allSlides[0];
    }

    if (!targetSlide) return;

    // If already visible, nothing to do
    if (!targetSlide.hasAttribute('hidden') && !targetSlide.hasAttribute('reveal')) {
      return;
    }

    // Hide all slides
    allSlides.forEach(slide => {
      slide.setAttribute('hidden', '');
      slide.setAttribute('aria-hidden', 'true');
      slide.removeAttribute('reveal');
    });

    // Show the target slide
    targetSlide.removeAttribute('hidden');
    targetSlide.removeAttribute('reveal');
    targetSlide.setAttribute('aria-hidden', 'false');

    // Reset scroller scroll position so target snaps into view
    if (scroller.scrollTo) {
      try {
        scroller.scrollTo({ left: 0, behavior: 'auto' });
      } catch (e) {
        scroller.scrollLeft = 0;
      }
    }

    // Also update the slideshow-component's internal current index if it exists
    const slideshowComponent = gallery.querySelector('slideshow-component');
    if (slideshowComponent) {
      // Update its internal refs list to reflect the visible slide
      // (Read-only for us, but the component will re-derive `slides` from the DOM)
      const visibleSlides = Array.from(allSlides).filter(
        s => !s.hasAttribute('hidden') || s.hasAttribute('reveal')
      );
      if (visibleSlides.length && visibleSlides[0]) {
        // Nudge any internal aria state
        visibleSlides[0].setAttribute('aria-hidden', 'false');
      }
    }

    console.log(
      '🖼️ VariantPickerCard: Switched image to slide:',
      targetSlide.getAttribute('slide-id'),
      '| variant:', variant.title
    );
  }

  switchPrice(variant) {
    const formatPrice = (cents) => {
      return 'Rs. ' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const prices = this.productCard.querySelectorAll('product-price');
    
    prices.forEach(priceComp => {
      const container = priceComp.querySelector('[ref="priceContainer"]');
      if (!container) return;

      let html;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html = `
          <span role="group">
            <span class="visually-hidden">Regular price&nbsp;</span>
            <span class="compare-at-price">MRP : ${formatPrice(variant.compare_at_price)}<br></span>
          </span>
          <span role="group">
            <span class="visually-hidden">Sale price&nbsp;</span>
            <span class="price">Our Price : ${formatPrice(variant.price)}</span>
          </span>
        `;
      } else {
        html = `<span class="price">Our Price : ${formatPrice(variant.price)}</span>`;
      }
      
      container.innerHTML = html;
    });
    
    console.log('💰 VariantPickerCard: Price updated to', formatPrice(variant.price));
  }

  updateProductForms(variant) {
    const variantInputs = this.productCard.querySelectorAll('[ref="variantId"]');
    
    variantInputs.forEach(input => {
      if (input.value !== variant.id.toString()) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
    
    console.log('📝 VariantPickerCard: Updated', variantInputs.length, 'form inputs to variant ID:', variant.id);
  }

  updateProductCardLinks(variant) {
    const productLinks = this.productCard.querySelectorAll('a[href*="/products/"]');
    const baseUrl = productLinks[0]?.href?.split('?')[0];
    
    if (baseUrl) {
      const newUrl = `${baseUrl}?variant=${variant.id}`;
      productLinks.forEach(link => {
        if (link.href !== newUrl) {
          link.href = newUrl;
        }
      });
      console.log('🔗 VariantPickerCard: Updated', productLinks.length, 'product links');
    }
  }
}

// Register the custom element
if (!customElements.get('variant-picker-card')) {
  customElements.define('variant-picker-card', VariantPickerCard);
  console.log('✅ VariantPickerCard: Custom element registered');
}

export default VariantPickerCard;